import { trpc } from "../../trpc";
import type { RoleAssignmentItem } from "../../../shared/scriptRoleAssignment";
import { SCRIPT_ROLE_MATCH_JOB_STALE_MS } from "../../../shared/scriptRoleAssignmentCache";
import type {
  ScriptAssignmentMatchJob,
  ScriptAssignmentMatchProgressSnapshot,
  ScriptAssignmentSessionCache,
  ScriptAssignmentTempRole,
  ScriptRoleAssignmentCacheKey,
} from "../../../shared/scriptRoleAssignmentCache";
import type { ParsedScriptSheetRow } from "../../../shared/feishuScriptSheet";
import type { AssignmentResult, SheetReadResult } from "./types";

export const MATCH_POLL_INTERVAL_MS = 1000;
export const MATCH_POLL_TIMEOUT_MS = 25 * 60 * 1000;
export const MATCH_STALL_MS = SCRIPT_ROLE_MATCH_JOB_STALE_MS;
export const MATCH_JOB_MISSING_MAX_POLLS = 30;

const ACTIVE_MATCH_JOB_STATUSES = ["pending", "scoring", "selecting"] as const;

function isActiveMatchJob(job: ScriptAssignmentMatchJob) {
  return ACTIVE_MATCH_JOB_STATUSES.includes(
    job.status as (typeof ACTIVE_MATCH_JOB_STATUSES)[number]
  );
}

export function resolveMatchPollingFatalError(input: {
  snapshot: ScriptAssignmentMatchProgressSnapshot;
  pollsWithoutJob: number;
  now?: number;
}): string | null {
  const { snapshot } = input;
  if (!snapshot.found) {
    return "未找到匹配任务，将自动重试";
  }
  if (!snapshot.fingerprintMatched) {
    return "剧本内容已变更，请重新读取后重试";
  }
  if (!snapshot.matchJob) {
    return input.pollsWithoutJob >= MATCH_JOB_MISSING_MAX_POLLS
      ? "匹配任务启动超时，将自动重试"
      : null;
  }
  if (!isActiveMatchJob(snapshot.matchJob)) {
    return null;
  }

  const activityAt = resolveMatchJobLastActivityAt(snapshot);
  const now = input.now ?? Date.now();
  if (activityAt <= 0 || now - activityAt > MATCH_STALL_MS) {
    return "匹配任务长时间无进展，将自动重试";
  }
  return null;
}

function progressAssignmentsToFull(
  items: ScriptAssignmentMatchProgressSnapshot["assignments"]
): RoleAssignmentItem[] {
  return items.map((item) => ({
    ...item,
    scoreBreakdown: [],
    selectionSource: item.selectionSource,
  }));
}

function snapshotToSessionView(
  snapshot: ScriptAssignmentMatchProgressSnapshot,
  input: {
    cacheLookup: ScriptRoleAssignmentCacheKey;
    tempRoles: ScriptAssignmentTempRole[];
  }
): ScriptAssignmentSessionCache {
  return {
    spreadsheetToken: input.cacheLookup.spreadsheetToken,
    sheetId: input.cacheLookup.sheetId,
    group: input.cacheLookup.group,
    sourceFingerprint: input.cacheLookup.sourceFingerprint,
    imageTextsByFileToken: {},
    tempRoles: input.tempRoles,
    memberCount: snapshot.memberCount,
    personas: {},
    assignments: progressAssignmentsToFull(snapshot.assignments),
    matchJob: snapshot.matchJob ?? undefined,
    updatedAt: snapshot.updatedAt ?? new Date().toISOString(),
  };
}

function buildMatchProgressSignature(snapshot: ScriptAssignmentMatchProgressSnapshot): string {
  const job = snapshot.matchJob;
  return [
    job?.status ?? "none",
    job?.scoringDone ?? 0,
    job?.selectionDone ?? 0,
    snapshot.assignmentCount,
    job?.lastHeartbeatAt ?? snapshot.updatedAt ?? "",
    job?.lastActivityTempRoleId ?? "",
  ].join(":");
}

function resolveMatchJobLastActivityAt(snapshot: ScriptAssignmentMatchProgressSnapshot): number {
  const job = snapshot.matchJob;
  const raw = job?.lastHeartbeatAt ?? snapshot.updatedAt;
  if (!raw) return 0;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

export interface MatchPollingCallbacks {
  onProgress?: (input: {
    done: number;
    total: number;
    label: string;
    activeTempRoleId?: string | null;
  }) => void;
}

export async function waitForMatchProgressViaPolling(input: {
  cacheLookup: ScriptRoleAssignmentCacheKey;
  tempRoles: ScriptAssignmentTempRole[];
  targetAssignmentCount: number;
  getEarlyError?: () => string | null;
  result: SheetReadResult;
  selectedGroup: string;
  parsedRows: ParsedScriptSheetRow[];
  callbacks?: MatchPollingCallbacks;
}): Promise<ScriptAssignmentSessionCache> {
  const applyProgressToUi = (
    snapshot: ScriptAssignmentMatchProgressSnapshot,
    pollError: string | null
  ) => {
    const cache = snapshotToSessionView(snapshot, {
      cacheLookup: input.cacheLookup,
      tempRoles: input.tempRoles,
    });
    const matchJob = cache.matchJob;
    const doneCount = cache.assignments.length;

    let currentLabel = `匹配气氛员中（${doneCount}/${input.targetAssignmentCount}）`;
    if (matchJob?.status === "pending") {
      currentLabel = `准备匹配…（0/${matchJob.totalRoles}）`;
    } else if (matchJob?.status === "scoring") {
      currentLabel = `分析候选气氛员…（${matchJob.scoringDone}/${matchJob.totalRoles}）`;
    } else if (matchJob?.status === "selecting") {
      currentLabel = `分配角色中…（${matchJob.selectionDone}/${matchJob.totalRoles}）`;
    } else if (matchJob?.status === "completed") {
      currentLabel =
        (matchJob.failures.length ?? 0) > 0
          ? `匹配完成，${matchJob.failures.length} 项失败`
          : `匹配完成（${doneCount}/${input.targetAssignmentCount}）`;
    } else if (matchJob?.status === "failed") {
      currentLabel = matchJob.error ?? "匹配失败";
    } else if (!snapshot.found) {
      currentLabel = "未找到匹配任务";
    } else if (!snapshot.fingerprintMatched) {
      currentLabel = "剧本内容已变更，请重新读取";
    }

    if (pollError) {
      currentLabel = pollError;
    }

    input.callbacks?.onProgress?.({
      done: doneCount,
      total: input.targetAssignmentCount,
      label: currentLabel,
      activeTempRoleId: matchJob?.lastActivityTempRoleId ?? null,
    });
  };

  return new Promise((resolve, reject) => {
    let active = true;
    let pollInFlight = false;
    let interval: number | null = null;
    let pollCount = 0;
    let pollsWithoutJob = 0;
    const pollStartedAt = Date.now();
    let lastProgressSignature = "";
    let lastProgressChangeAt = Date.now();

    const clearPolling = () => {
      if (interval !== null) {
        window.clearInterval(interval);
        interval = null;
      }
    };

    const rejectPolling = (message: string) => {
      if (!active) return;
      active = false;
      clearPolling();
      reject(new Error(message));
    };

    const resolvePolling = (cache: ScriptAssignmentSessionCache) => {
      if (!active) return;
      active = false;
      clearPolling();
      resolve(cache);
    };

    const poll = async () => {
      if (!active || pollInFlight) return;
      pollInFlight = true;
      pollCount += 1;

      try {
        const earlyError = input.getEarlyError?.();
        if (earlyError) {
          rejectPolling(earlyError);
          return;
        }

        if (Date.now() - pollStartedAt > MATCH_POLL_TIMEOUT_MS) {
          rejectPolling("匹配超时，请稍后重试");
          return;
        }

        const snapshot = await trpc.feishuSheets.getRoleAssignmentMatchProgress.query(
          input.cacheLookup
        );
        if (!active) return;

        let pollError: string | null = null;
        if (!snapshot.found) {
          pollError = "未找到匹配任务";
          pollsWithoutJob += 1;
        } else if (!snapshot.fingerprintMatched) {
          pollError = "剧本内容已变更，请重新读取";
          pollsWithoutJob += 1;
        } else if (!snapshot.matchJob) {
          pollsWithoutJob += 1;
          pollError =
            pollsWithoutJob >= MATCH_JOB_MISSING_MAX_POLLS
              ? "匹配任务启动超时，将自动重试"
              : null;
        } else {
          pollsWithoutJob = 0;
          const signature = buildMatchProgressSignature(snapshot);
          if (signature !== lastProgressSignature) {
            lastProgressSignature = signature;
            lastProgressChangeAt = Date.now();
          }
        }

        const fatalError = resolveMatchPollingFatalError({
          snapshot,
          pollsWithoutJob,
        });
        if (fatalError) {
          pollError = fatalError;
        }

        applyProgressToUi(snapshot, pollError);

        if (fatalError) {
          rejectPolling(fatalError);
          return;
        }

        const jobStatus = snapshot.matchJob?.status;
        if (jobStatus === "completed" || jobStatus === "failed") {
          let fullCache: ScriptAssignmentSessionCache | null;
          try {
            fullCache = await trpc.feishuSheets.getRoleAssignmentCache.query(input.cacheLookup);
          } catch (error: unknown) {
            rejectPolling(error instanceof Error ? error.message : "匹配结果加载失败");
            return;
          }
          if (!fullCache) {
            rejectPolling("匹配已完成但无法加载结果");
            return;
          }
          if (jobStatus === "failed") {
            rejectPolling(snapshot.matchJob?.error ?? "匹配失败");
            return;
          }
          resolvePolling(fullCache);
        }
      } catch (error: unknown) {
        const msg = error instanceof Error ? error.message : "进度查询失败";
        if (Date.now() - lastProgressChangeAt > MATCH_STALL_MS) {
          rejectPolling(msg);
        }
      } finally {
        pollInFlight = false;
      }
    };

    interval = window.setInterval(() => {
      void poll();
    }, MATCH_POLL_INTERVAL_MS);
    void poll();
  });
}

export async function finalizeAssignmentResult(input: {
  result: SheetReadResult;
  sourceFingerprint: string;
  selectedGroup: string;
  parsedRows: ParsedScriptSheetRow[];
  imageTextsByFileToken: Record<string, string>;
  personas: import("../../../shared/scriptRoleAssignment").ScriptRolePersona[];
  assignments: RoleAssignmentItem[];
  overwriteMode: boolean;
}): Promise<AssignmentResult> {
  return trpc.feishuSheets.finalizeRoleAssignment.mutate({
    spreadsheetToken: input.result.spreadsheetToken,
    sheetId: input.result.sheetId,
    sourceFingerprint: input.sourceFingerprint,
    group: input.selectedGroup,
    parsedRows: input.parsedRows,
    imageTextsByFileToken: input.imageTextsByFileToken,
    personas: input.personas,
    assignments: input.assignments,
    overwriteMode: input.overwriteMode,
  });
}
